import { AiOutlineCloudSync } from 'react-icons/ai';
import { BsBluetooth, BsUniversalAccessCircle } from 'react-icons/bs';
import { Container, Modal } from 'react-bootstrap';
import { Dispatch, FC, SetStateAction } from 'react';
import { FaKey } from 'react-icons/fa';
import { helpers, userServices } from 'src/services';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { Hr } from 'src/components/_commons';
import { IoIosWarning } from 'react-icons/io';
import { MdDangerous } from 'react-icons/md';
import { startRegistration } from '@simplewebauthn/browser';
import { typeUserWebAuthnAuthenticator } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetCurrentUser, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    closeModal: () => void;
    setRefreshCounter: Dispatch<SetStateAction<number>>;
};

export const UserPasskeys: FC<propsType> = ({ closeModal, setRefreshCounter }) => {
    const dispatch = useDispatch();
    const isMobile = useGetIsMobile();
    const user = useGetCurrentUser();

    const createPasskey = async () => {
        dispatch(showLoadingModalReducer());
        const response = await userServices.PasskeyStartRegistration();
        dispatch(hideLoadingModalReducer());
        if (!response.success || !response.data?.passkeyChallengeObject) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        const challenge = response.data.passkeyChallengeObject.challenge;
        let attestationResponse;
        try {
            attestationResponse = await startRegistration({
                optionsJSON: response.data.passkeyChallengeObject,
                useAutoRegister: false
            });
        } catch (error) {
            console.error(error);
            return;
        }
        dispatch(showLoadingModalReducer());
        const confirmResponse = await userServices.PasskeyConfirmRegistration(attestationResponse, challenge);
        dispatch(hideLoadingModalReducer());
        dispatch(setAlertResponse({ response: confirmResponse }));
        setRefreshCounter(x => x + 1);
    }

    const deletePasskey = (passkey: typeUserWebAuthnAuthenticator) => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: 'Confirmar',
            message: `Se va a eliminar la llave de acceso ${passkey.authenticatorName} (${helpers.TimeConverter(passkey.createdAt ?? 0, true)}.) de la aplicación y ya no se podrá usar para ingresar. Si estaba sincronizada en diferentes dispositivos, ya no se podrá usar en ninguno. Es conveniente eliminarla también de '${passkey.authenticatorName}'.`,
            animation: 3,
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await userServices.RevokePasskey(passkey.id);
                dispatch(hideLoadingModalReducer());
                dispatch(setAlertResponse({ response }));
                setRefreshCounter(x => x + 1);
            }
        }));
    }

    return (
        <Modal
            centered
            fullscreen={'md-down'}
            onHide={closeModal}
            show
            size={'lg'}
        >
            <style>
                {'.btn-close { background-color: white; }'}
            </style>
            <Modal.Header className={'bg-dark text-white'} closeButton>
                <Modal.Title>
                    LLAVES DE ACCESO
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={'bg-lightdark text-white'}>
                <Container style={{ maxWidth: '700px', padding: isMobile ? '25px 30px 0' : '25px 0' }}>
                    <div style={{ marginBottom: '25px' }}>
                        <h2 className={'bg-dark border-top border-bottom text-center py-2'}>
                            LLAVES DISPONIBLES
                        </h2>
                    </div>
                    <div className={'mx-auto'} style={{ maxWidth: '400px' }}>
                        {!!user.webAuthnAuthenticators?.length ?
                            user.webAuthnAuthenticators?.map(p =>
                                <div key={p.id} className={`card border bg-general-blue text-white my-3`}>
                                    <div className={'card-header d-flex justify-content-between align-items-center ps-3 pe-2'}>
                                        <div>
                                            <div className={'fw-bold'}>
                                                <FaKey size={16} className={'me-2'} />{p.authenticatorName}
                                            </div>
                                            <div className={'small'} style={{ marginLeft: '24px' }}>
                                                {helpers.TimeConverter(p.createdAt ?? 0, true)}.
                                            </div>
                                            <div className={'small'} style={{ fontSize: '0.6rem', marginLeft: '24px' }}>
                                                {new URL(p.origin).hostname}
                                            </div>
                                        </div>
                                        <button className={'btn btn-general-blue border btn-sm'}
                                            onClick={() => deletePasskey(p)}
                                        >
                                            ELIMINAR
                                        </button>
                                    </div>
                                </div>
                            )
                            :
                            <div className={'text-center'}>
                                No hay ninguna
                            </div>
                        }
                    </div>
                    <div style={{ marginBlock: isMobile ? '35px' : '30px' }}>
                        <Hr  />
                    </div>
                    <div className={'mb-4'}>
                        <button className={'btn btn-general-red fw-bold d-block py-2 mx-auto'}
                            onClick={createPasskey}
                        >
                            CREAR UNA LLAVE DE ACCESO PARA ESTE DISPOSITIVO
                        </button>
                    </div>
                    <div>
                        <ul className={'small'}>
                            <li className={'my-2'}>
                                <FaKey size={16} /> Una llave de acceso es una credencial exclusiva de esta aplicación y usuario que se guarda en el dispositivo y que permite ingresar al sitio utilizando el método de desbloqueo que se usa en el dispositivo sin necesidad de recordar el usuario ni la contraseña del sitio: reconocimiento facial o dactilar, contraseña PIN o patrón de 9 puntos.
                            </li>
                            <li className={'my-2'}>
                                <BsUniversalAccessCircle size={18} className={'mb-1'} /> Puede ser usada indistintamente desde todos los navegadores instalados, incluso en modo incógnito.
                            </li>
                            <li className={'my-2'}>
                                <AiOutlineCloudSync size={20} className={'mb-1'} /> Las llaves de acceso puede que sean sincronizadas automáticamente por Google, Microsoft o Apple en todos tus dispositivos (celulares, computadoras, tablets).
                            </li>
                            <li className={'my-2'}>
                                <BsBluetooth size={16} className='mb-1' /> Puede que el dispositivo ofrezca también la opción de crear una llave de acceso mediante conexión Bluetooth y código QR a otro dispositivo o mediante una llave física de seguridad.
                            </li>
                            <li className={'fw-bold my-2'}>
                                <MdDangerous size={18} className={'text-danger mb-1'} /> NUNCA CREAR UNA LLAVE DE ACCESO EN UN DISPOSITIVO PRESTADO.
                            </li>
                            <li className={'my-2'}>
                                <IoIosWarning size={18} className={'text-warning mb-1'} /> Si el dispositivo es compartido con otra persona y se comparte sesión de sistema operativo, el acceso a esta aplicación va a estar compartido también.
                            </li>
                        </ul>
                    </div>
                </Container>
            </Modal.Body>
            <Modal.Footer className={'bg-dark'}>
                <button className={'btn btn-secondary'}
                    onClick={closeModal}
                >
                    Cerrar
                </button>
            </Modal.Footer>
        </Modal>
    );
}
